import React, { useEffect, useState } from "react";
import { IoStar, IoStarOutline } from "react-icons/io5";
import { supabase } from "../supabaseClient";

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [reviewer, setReviewer] = useState(null);
  const [status, setStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadReviews = async () => {
    const { data, error } = await supabase
      .from("community_reviews")
      .select("id, reviewer_name, rating, message, created_at")
      .order("created_at", { ascending: false })
      .limit(12);

    if (error) {
      console.error("Unable to load community reviews:", error);
      return;
    }
    setReviews(data || []);
  };

  useEffect(() => {
    const load = async () => {
      await loadReviews();

      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;

      const { data: user } = await supabase
        .from("users")
        .select("public_user_id, username")
        .eq("public_user_id", authData.user.id)
        .single();

      if (user) setReviewer(user);
    };

    load();
  }, []);

  const submitReview = async (event) => {
    event.preventDefault();
    if (!reviewer) {
      setStatus("Sign in to leave a review.");
      return;
    }

    const trimmedMessage = message.trim();
    if (trimmedMessage.length < 10) {
      setStatus("Please write at least 10 characters.");
      return;
    }

    setIsSaving(true);
    setStatus("");
    const { error } = await supabase.from("community_reviews").upsert({
      reviewer_id: reviewer.public_user_id,
      reviewer_name: reviewer.username || "FriendlyBets member",
      rating,
      message: trimmedMessage,
    }, { onConflict: "reviewer_id" });

    if (error) {
      console.error("Unable to save community review:", error);
      setStatus("Your review could not be saved. Please try again.");
    } else {
      setMessage("");
      setStatus("Thanks. Your review is now live.");
      await loadReviews();
    }
    setIsSaving(false);
  };

  return (
    <section className="reviews" aria-labelledby="reviews-title">
      <div className="reviews__intro">
        <p className="reviews__eyebrow">Community feedback</p>
        <h2 id="reviews-title">Hear It From Our Community</h2>
        <p>Share what makes game day more fun with FriendlyBets.</p>
      </div>

      <div className="reviews__layout">
        <form className="reviews__form" onSubmit={submitReview}>
          <label className="reviews__label">Your rating</label>
          <div className="reviews__stars" role="radiogroup" aria-label="Your rating">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                className={value <= rating ? "is-selected" : ""}
                onClick={() => setRating(value)}
                aria-label={`${value} star${value === 1 ? "" : "s"}`}
                aria-pressed={rating === value}
              >
                {value <= rating ? <IoStar /> : <IoStarOutline />}
              </button>
            ))}
          </div>
          <label className="reviews__label" htmlFor="community-review">Your review</label>
          <textarea
            id="community-review"
            value={message}
            maxLength={500}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Tell the community what you enjoy about FriendlyBets."
          />
          <div className="reviews__form-footer">
            <span>{message.length}/500</span>
            <button type="submit" className="fb-btn fb-btn--primary" disabled={isSaving}>
              {isSaving ? "Saving..." : "Publish review"}
            </button>
          </div>
          {status && <p className="reviews__status" role="status">{status}</p>}
        </form>

        <div className="reviews__list" aria-live="polite">
          {reviews.length === 0 ? (
            <p className="reviews__empty">Be the first to share a review.</p>
          ) : (
            reviews.map((review) => (
              <article className="reviews__item" key={review.id}>
                <div className="reviews__item-heading">
                  <strong>{review.reviewer_name}</strong>
                  <span aria-label={`${review.rating} out of 5 stars`}>
                    {"★".repeat(review.rating)}
                  </span>
                </div>
                <p>{review.message}</p>
              </article>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
